import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useCrud } from '../lib/useCrud';
import { Autocomplete } from '../components/Autocomplete';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ClientLink } from '../components/links/EntityLinks';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Edit, Trash2, MoreVertical, KeyRound, Loader2, Eye, EyeOff, Copy, Search, Users, Shield, Clock, History, AlertTriangle, Info, CalendarClock, Infinity as InfinityIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';

const typeOptions = [
  { value: 'cloud', label: 'Cloud' }, { value: 'hosting', label: 'Hosting' },
  { value: 'development', label: 'Development' }, { value: 'email', label: 'Email' },
  { value: 'dns', label: 'DNS' }, { value: 'database', label: 'Database' },
  { value: 'api', label: 'API Key' }, { value: 'other', label: 'Other' },
];

const EVENT_LABELS = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  password_viewed: 'Password viewed',
  access_granted: 'Access granted',
  access_revoked: 'Access revoked',
  access_expiry_changed: 'Expiry changed',
};

const EVENT_TONE = {
  created: 'bg-emerald-500/10 text-emerald-500',
  updated: 'bg-blue-500/10 text-blue-500',
  deleted: 'bg-red-500/10 text-red-500',
  password_viewed: 'bg-amber-500/10 text-amber-500',
  access_granted: 'bg-emerald-500/10 text-emerald-500',
  access_revoked: 'bg-red-500/10 text-red-500',
  access_expiry_changed: 'bg-cyan-500/10 text-cyan-500',
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const isExpired = (iso) => !!iso && new Date(iso).getTime() < Date.now();

export const CredentialsPage = () => {
  const { user, hasPermission } = useAuth();
  const confirm = useConfirmation();

  const canCreate = hasPermission('credentials.create');
  const canEdit = hasPermission('credentials.edit');
  const canDelete = hasPermission('credentials.delete');
  const canManageAccess = hasPermission('credentials.manage_access');

  const { data: creds, loading, fetchData, create, update, remove } = useCrud('/credentials');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const [selectedCred, setSelectedCred] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  // accessList entries: { user_id, expires_at|null }
  const [accessList, setAccessList] = useState([]);
  const [formData, setFormData] = useState({ title: '', type: 'other', client_name: '', username: '', password: '', url: '', notes: '' });
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [auditCred, setAuditCred] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsCred, setDetailsCred] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users');
      if (res.data.success) setAllUsers(res.data.data);
    } catch { /* admin users fetch is best-effort */ }
  }, []);

  useEffect(() => { if (canManageAccess) fetchUsers(); }, [canManageAccess, fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedCred) {
        await update(selectedCred.id, formData);
        toast.success('Credential updated');
      } else {
        await create(formData);
        toast.success('Credential added');
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (c) => {
    const ok = await confirm({ title: 'Delete Credential', description: `Delete "${c.title}"? This action cannot be undone.` });
    if (!ok) return;
    try {
      await remove(c.id);
      toast.success('Credential deleted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleEdit = (c) => {
    setSelectedCred(c);
    setFormData({ title: c.title, type: c.type, client_name: c.client_name, username: c.username, password: '', url: c.url || '', notes: c.notes || '' });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleManageAccess = (c) => {
    setSelectedCred(c);
    // Always coerce to new shape (legacy strings handled by backend on save)
    const acl = (c.access_list || []).map((e) => (
      typeof e === 'string' ? { user_id: e, expires_at: null } : { user_id: e.user_id, expires_at: e.expires_at || null }
    ));
    setAccessList(acl);
    setIsAccessOpen(true);
  };

  const handleSaveAccess = async () => {
    try {
      await api.put(`/credentials/${selectedCred.id}/access`, { access_list: accessList });
      toast.success('Access updated');
      setIsAccessOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update access'); }
  };

  const toggleAccess = (userId) => {
    setAccessList((prev) => {
      const found = prev.find((e) => e.user_id === userId);
      if (found) return prev.filter((e) => e.user_id !== userId);
      return [...prev, { user_id: userId, expires_at: null }];
    });
  };

  const setExpiry = (userId, dateStr) => {
    setAccessList((prev) => prev.map((e) => e.user_id === userId
      ? { ...e, expires_at: dateStr ? new Date(`${dateStr}T23:59:59`).toISOString() : null }
      : e));
  };

  const openAuditLog = async (c) => {
    setAuditCred(c);
    setIsAuditOpen(true);
    setAuditRows([]);
    setAuditLoading(true);
    try {
      const res = await api.get(`/credentials/${c.id}/audit?limit=200`);
      if (res.data.success) setAuditRows(res.data.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load audit log'); }
    finally { setAuditLoading(false); }
  };

  const openDetails = (c) => { setDetailsCred(c); setIsDetailsOpen(true); };

  // Compute the current user's own grant entry for a given credential (used
  // to show "your expiry" chip + a small banner inside the details modal).
  const myGrant = (c) => {
    if (!c) return null;
    return (c.access_list || []).find((e) => e && e.user_id === user?.id) || null;
  };

  const revealPassword = async (credId) => {
    try {
      const res = await api.get(`/credentials/${credId}/password`);
      if (res.data.success) {
        setVisiblePasswords((prev) => ({ ...prev, [credId]: res.data.password }));
        setTimeout(() => setVisiblePasswords((prev) => { const n = { ...prev }; delete n[credId]; return n; }), 10000);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Access denied'); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  const resetForm = () => { setFormData({ title: '', type: 'other', client_name: '', username: '', password: '', url: '', notes: '' }); setSelectedCred(null); setIsEditing(false); setShowFormPassword(false); };

  const getTypeBadge = (type) => {
    const cfg = { cloud: 'bg-blue-500/10 text-blue-500', hosting: 'bg-purple-500/10 text-purple-500', development: 'bg-emerald-500/10 text-emerald-500', email: 'bg-amber-500/10 text-amber-500', dns: 'bg-cyan-500/10 text-cyan-500', database: 'bg-red-500/10 text-red-500', api: 'bg-orange-500/10 text-orange-500', other: 'bg-gray-500/10 text-gray-400' };
    return <Badge className={cfg[type] || cfg.other}>{type}</Badge>;
  };

  const filtered = creds.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.title?.toLowerCase().includes(q) || c.client_name?.toLowerCase().includes(q) || c.username?.toLowerCase().includes(q);
  });

  const userById = (id) => allUsers.find((u) => u.id === id);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="credentials-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Credential Vault</h1>
          <p className="text-muted-foreground">Share secrets with specific team members — time-bound access with full audit trail.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
          {canCreate && (
          <DialogTrigger asChild><Button data-testid="add-credential-btn"><Plus className="h-4 w-4 mr-2" />Add Credential</Button></DialogTrigger>
          )}
          <DialogContent className="max-w-lg" data-testid="credential-dialog">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit Credential' : 'Add Credential'}</DialogTitle><DialogDescription>Store credentials securely. Share via &quot;Manage Access&quot; after saving.</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              {/* Hidden dummy fields — Chromium/Safari ignore autoComplete=off
                  on username/password when they follow a form pattern.
                  Two decoy inputs above the real ones absorb the browser's
                  autofill so operator-entered creds don't get overwritten
                  by whatever's saved in the browser's own password vault. */}
              <input type="text" name="fake-user" autoComplete="username" className="hidden" tabIndex={-1} aria-hidden="true" />
              <input type="password" name="fake-pass" autoComplete="new-password" className="hidden" tabIndex={-1} aria-hidden="true" />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2"><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="cred-title" /></div>
                <div className="space-y-2"><Label>Type</Label><Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}><SelectTrigger data-testid="cred-type"><SelectValue /></SelectTrigger><SelectContent>{typeOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Client</Label>
                  <Autocomplete
                    value={formData.client_name}
                    onChange={(v) => setFormData({ ...formData, client_name: v })}
                    fetchOptions={async (q) => { const res = await api.get(`/clients?search=${q}&limit=10`); return res.data.success ? res.data.data.map((c) => c.company_name || c.name) : []; }}
                    placeholder="Search clients..."
                    data-testid="cred-client"
                  />
                </div>
                <div className="space-y-2"><Label>Username</Label><Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} data-testid="cred-username" name="cred-user-field" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} /></div>
                <div className="space-y-2"><Label>Password</Label>
                  <div className="relative">
                    <Input
                      type={showFormPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={isEditing ? 'Leave blank to keep current' : ''}
                      data-testid="cred-password"
                      name="cred-secret-field"
                      autoComplete="new-password"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      className="pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowFormPassword((v) => !v)}
                      className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                      aria-label={showFormPassword ? 'Hide password' : 'Show password'}
                      data-testid="cred-password-toggle"
                    >
                      {showFormPassword
                        ? <EyeOff className="h-4 w-4" />
                        : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2 col-span-2"><Label>URL</Label><Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://" /></div>
                <div className="space-y-2 col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any login instructions, 2FA recovery codes, special steps, account context…"
                    rows={5}
                    className="resize-y min-h-[120px]"
                    data-testid="cred-notes"
                  />
                </div>
              </div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="cred-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card><CardContent className="py-4"><div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search credentials..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="cred-search" /></div></CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><KeyRound className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="cred-total">{filtered.length}</p><p className="text-xs text-muted-foreground">Credentials</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><KeyRound className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold">{new Set(filtered.map((c) => c.type)).size}</p><p className="text-xs text-muted-foreground">Categories</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Shield className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{filtered.filter((c) => (c.access_list || []).length > 0).length}</p><p className="text-xs text-muted-foreground">Shared</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        filtered.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><KeyRound className="h-12 w-12 mb-4 opacity-50" /><p>No credentials found</p></div> :
        <Table data-testid="credentials-table"><TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Client</TableHead><TableHead>Username</TableHead><TableHead>Password</TableHead><TableHead>Access</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{filtered.map((c) => {
          const acl = c.access_list || [];
          const activeGrants = acl.filter((e) => !e.expires_at || !isExpired(e.expires_at));
          const expiredGrants = acl.length - activeGrants.length;
          const myEntry = myGrant(c);
          const myExpiry = myEntry?.expires_at || null;
          const myExpired = !!myExpiry && isExpired(myExpiry);
          const hasNotes = !!(c.notes && String(c.notes).trim());
          return (
          <TableRow key={c.id} data-testid={`cred-row-${c.id}`}>
            <TableCell>
              <div>
                <button
                  type="button"
                  onClick={() => openDetails(c)}
                  className="font-medium text-left hover:text-primary hover:underline focus:outline-none focus-visible:underline inline-flex items-center gap-1.5"
                  title="View details"
                  data-testid={`cred-title-${c.id}`}
                >
                  {c.title}
                  {hasNotes && <Info className="h-3 w-3 text-muted-foreground" />}
                </button>
                {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer" className="block text-xs text-primary hover:underline truncate max-w-[240px]">{c.url}</a>}
              </div>
            </TableCell>
            <TableCell>{getTypeBadge(c.type)}</TableCell>
            <TableCell><ClientLink id={c.client_id} name={c.client_name} /></TableCell>
            <TableCell><div className="flex items-center gap-1"><span className="font-mono text-sm">{c.username}</span><button onClick={() => copyToClipboard(c.username, 'Username')} className="p-1 hover:bg-muted rounded"><Copy className="h-3 w-3 text-muted-foreground" /></button></div></TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <span className="font-mono text-sm">{visiblePasswords[c.id] || '••••••••'}</span>
                <button onClick={() => visiblePasswords[c.id] ? setVisiblePasswords((p) => { const n = { ...p }; delete n[c.id]; return n; }) : revealPassword(c.id)} className="p-1 hover:bg-muted rounded" data-testid={`reveal-pwd-${c.id}`}>
                  {visiblePasswords[c.id] ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
                </button>
                {visiblePasswords[c.id] && <button onClick={() => copyToClipboard(visiblePasswords[c.id], 'Password')} className="p-1 hover:bg-muted rounded"><Copy className="h-3 w-3 text-muted-foreground" /></button>}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1.5 flex-wrap">
                {canManageAccess ? (
                  <>
                    <Badge className="bg-muted text-muted-foreground text-xs" data-testid={`access-count-${c.id}`}>
                      <Users className="h-3 w-3 mr-1" />{activeGrants.length}
                    </Badge>
                    {expiredGrants > 0 && (
                      <Badge className="bg-red-500/10 text-red-500 text-xs" title={`${expiredGrants} expired grant${expiredGrants > 1 ? 's' : ''}`}>
                        <AlertTriangle className="h-3 w-3 mr-1" />{expiredGrants} expired
                      </Badge>
                    )}
                  </>
                ) : (
                  // Non-admin: show only the current user's own grant status
                  myEntry ? (
                    myExpired ? (
                      <Badge className="bg-red-500/10 text-red-500 text-xs" data-testid={`my-expiry-${c.id}`}>
                        <AlertTriangle className="h-3 w-3 mr-1" />Expired {fmtDateTime(myExpiry).split(',')[0]}
                      </Badge>
                    ) : myExpiry ? (
                      <Badge className="bg-amber-500/10 text-amber-600 text-xs" title={`Access expires ${fmtDateTime(myExpiry)}`} data-testid={`my-expiry-${c.id}`}>
                        <CalendarClock className="h-3 w-3 mr-1" />Until {fmtDateTime(myExpiry).split(',')[0]}
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-500/10 text-emerald-600 text-xs" title="Permanent access" data-testid={`my-expiry-${c.id}`}>
                        <InfinityIcon className="h-3 w-3 mr-1" />Permanent
                      </Badge>
                    )
                  ) : c.created_by === user?.id ? (
                    <Badge className="bg-emerald-500/10 text-emerald-600 text-xs" data-testid={`my-expiry-${c.id}`}>
                      <Shield className="h-3 w-3 mr-1" />Owner
                    </Badge>
                  ) : null
                )}
              </div>
            </TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`cred-actions-${c.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openDetails(c)} data-testid={`cred-view-${c.id}`}><Info className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
                {canEdit && (<DropdownMenuItem onClick={() => handleEdit(c)} data-testid={`cred-edit-${c.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                {canManageAccess && (<DropdownMenuItem onClick={() => handleManageAccess(c)} data-testid={`cred-share-${c.id}`}><Users className="h-4 w-4 mr-2" />Manage Access</DropdownMenuItem>)}
                {canManageAccess && (<DropdownMenuItem onClick={() => openAuditLog(c)} data-testid={`cred-audit-${c.id}`}><History className="h-4 w-4 mr-2" />View Audit Log</DropdownMenuItem>)}
                {canDelete && (<DropdownMenuItem onClick={() => handleDelete(c)} className="text-destructive focus:text-destructive" data-testid={`cred-delete-${c.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>);
        })}</TableBody></Table>}
      </CardContent></Card>

      {/* Access Management Dialog — per-user time-bound grants */}
      <Dialog open={isAccessOpen} onOpenChange={setIsAccessOpen}>
        <DialogContent className="max-w-xl" data-testid="access-dialog">
          <DialogHeader>
            <DialogTitle>Manage Access — {selectedCred?.title}</DialogTitle>
            <DialogDescription>
              Pick users who can view this credential. Optionally set an expiry date to auto-revoke access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto py-2 pr-1">
            {/*
              Render-time active filter. The `allUsers` fetch is intentionally
              unfiltered (line 87) so that historical "Granted by X" lookups
              keep resolving even for users who later went inactive. But the
              live grant-access checklist must only offer currently-active
              colleagues — otherwise inactive users keep showing up here.
            */}
            {allUsers.filter((u) => u.id !== user?.id && u.status === 'active').map((u) => {
              const entry = accessList.find((e) => e.user_id === u.id);
              const isOn = !!entry;
              const expiryDate = entry?.expires_at ? entry.expires_at.slice(0, 10) : '';
              const expired = entry?.expires_at && isExpired(entry.expires_at);
              return (
                <div key={u.id} className={`flex items-center gap-3 p-2 rounded-lg border ${isOn ? 'border-primary/30 bg-primary/5' : 'border-transparent hover:bg-muted/20'}`} data-testid={`access-user-${u.id}`}>
                  <Checkbox checked={isOn} onCheckedChange={() => toggleAccess(u.id)} id={`access-${u.id}`} data-testid={`access-check-${u.id}`} />
                  <label htmlFor={`access-${u.id}`} className="flex-1 cursor-pointer min-w-0">
                    <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </label>
                  {isOn && (
                    <div className="flex items-center gap-2">
                      <Clock className={`h-3.5 w-3.5 ${expired ? 'text-red-500' : 'text-muted-foreground'}`} />
                      <Input
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiry(u.id, e.target.value)}
                        className={`h-8 w-[148px] text-xs ${expired ? 'border-red-500 text-red-500' : ''}`}
                        placeholder="No expiry"
                        data-testid={`access-expiry-${u.id}`}
                      />
                      {entry?.expires_at && (
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setExpiry(u.id, '')} title="Make permanent">
                          ✕
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {allUsers.filter((u) => u.id !== user?.id && u.status === 'active').length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No other users found</p>}
          </div>
          <DialogFooter>
            <p className="text-xs text-muted-foreground mr-auto">{accessList.length} user{accessList.length !== 1 ? 's' : ''} with access · leave date blank for permanent</p>
            <Button variant="outline" onClick={() => setIsAccessOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAccess} data-testid="save-access-btn">Save Access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={isAuditOpen} onOpenChange={setIsAuditOpen}>
        <DialogContent className="max-w-2xl" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle>Audit Log — {auditCred?.title}</DialogTitle>
            <DialogDescription>All access, edits, and password views are recorded here.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[480px] overflow-y-auto">
            {auditLoading ? (
              <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : auditRows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">No audit entries yet.</div>
            ) : (
              <Table data-testid="audit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">When</TableHead>
                    <TableHead className="w-[140px]">Event</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditRows.map((r) => {
                    const targetUser = r.target_user_id ? userById(r.target_user_id) : null;
                    const targetLabel = targetUser ? `${targetUser.first_name} ${targetUser.last_name}` : r.target_user_id;
                    return (
                      <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                        <TableCell className="text-xs text-muted-foreground">{fmtDateTime(r.timestamp)}</TableCell>
                        <TableCell><Badge className={`${EVENT_TONE[r.event] || 'bg-muted'} text-xs`}>{EVENT_LABELS[r.event] || r.event}</Badge></TableCell>
                        <TableCell className="text-sm">{r.actor_name || r.actor_email || r.actor_id}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.event === 'password_viewed' && 'Reveal-clicked'}
                          {r.event === 'access_granted' && (
                            <>Granted to <span className="text-foreground font-medium">{targetLabel}</span>{r.expires_at ? <> · expires {fmtDateTime(r.expires_at)}</> : <> · permanent</>}</>
                          )}
                          {r.event === 'access_revoked' && (<>Revoked from <span className="text-foreground font-medium">{targetLabel}</span></>)}
                          {r.event === 'access_expiry_changed' && (
                            <>Expiry for <span className="text-foreground font-medium">{targetLabel}</span> → {r.expires_at ? fmtDateTime(r.expires_at) : 'permanent'}</>
                          )}
                          {r.event === 'updated' && (
                            <>Fields: <span className="font-mono">{(r.details?.fields || []).join(', ') || '—'}</span>{r.details?.password_rotated && <> · 🔑 password rotated</>}</>
                          )}
                          {r.event === 'created' && <>Initial type: <span className="font-mono">{r.details?.type || '—'}</span></>}
                          {r.event === 'deleted' && 'Soft-deleted'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAuditOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog — read-only, includes notes + per-user grant info */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-lg" data-testid="details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailsCred?.title}
              {detailsCred && getTypeBadge(detailsCred.type)}
            </DialogTitle>
            <DialogDescription>Credential details, login instructions and your access info.</DialogDescription>
          </DialogHeader>
          {detailsCred && (() => {
            const mine = myGrant(detailsCred);
            const mineExpired = mine?.expires_at && isExpired(mine.expires_at);
            const granter = mine?.granted_by ? allUsers.find((u) => u.id === mine.granted_by) : null;
            const owner = detailsCred.created_by === user?.id;
            return (
              <div className="space-y-4 text-sm">
                {/* Your access banner */}
                <div className={`rounded-lg border p-3 ${mineExpired ? 'border-red-500/30 bg-red-500/5' : owner ? 'border-emerald-500/30 bg-emerald-500/5' : mine?.expires_at ? 'border-amber-500/30 bg-amber-500/5' : 'border-emerald-500/30 bg-emerald-500/5'}`} data-testid="details-my-access">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Your access</p>
                  {owner ? (
                    <p className="text-sm flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-emerald-500" />You created this credential — permanent access.</p>
                  ) : mineExpired ? (
                    <p className="text-sm flex items-center gap-1.5 text-red-600"><AlertTriangle className="h-3.5 w-3.5" />Your access expired on {fmtDateTime(mine.expires_at)}. Contact a vault admin to renew.</p>
                  ) : mine?.expires_at ? (
                    <p className="text-sm flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-amber-500" />Access expires on <span className="font-medium">{fmtDateTime(mine.expires_at)}</span>{granter ? <> · shared by {granter.first_name} {granter.last_name}</> : null}</p>
                  ) : mine ? (
                    <p className="text-sm flex items-center gap-1.5"><InfinityIcon className="h-3.5 w-3.5 text-emerald-500" />Permanent access{granter ? <> · shared by {granter.first_name} {granter.last_name}</> : null}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Vault admin view.</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {detailsCred.client_name && (
                    <div>
                      <p className="text-xs text-muted-foreground">Client</p>
                      <p className="font-medium"><ClientLink id={detailsCred.client_id} name={detailsCred.client_name} /></p>
                    </div>
                  )}
                  {detailsCred.username && (
                    <div>
                      <p className="text-xs text-muted-foreground">Username</p>
                      <p className="font-mono text-sm flex items-center gap-1">
                        {detailsCred.username}
                        <button onClick={() => copyToClipboard(detailsCred.username, 'Username')} className="p-1 hover:bg-muted rounded"><Copy className="h-3 w-3 text-muted-foreground" /></button>
                      </p>
                    </div>
                  )}
                  {detailsCred.url && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">URL</p>
                      <a href={detailsCred.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{detailsCred.url}</a>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-1">Password</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{visiblePasswords[detailsCred.id] || '••••••••••••'}</span>
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => visiblePasswords[detailsCred.id] ? setVisiblePasswords((p) => { const n = { ...p }; delete n[detailsCred.id]; return n; }) : revealPassword(detailsCred.id)}>
                        {visiblePasswords[detailsCred.id] ? <><EyeOff className="h-3.5 w-3.5 mr-1" />Hide</> : <><Eye className="h-3.5 w-3.5 mr-1" />Reveal</>}
                      </Button>
                      {visiblePasswords[detailsCred.id] && (
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => copyToClipboard(visiblePasswords[detailsCred.id], 'Password')}>
                          <Copy className="h-3.5 w-3.5 mr-1" />Copy
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Notes / login instructions</p>
                  {detailsCred.notes && String(detailsCred.notes).trim() ? (
                    <div className="rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap break-words max-h-[240px] overflow-y-auto" data-testid="details-notes">{detailsCred.notes}</div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No notes added.</p>
                  )}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>Close</Button>
            {canEdit && detailsCred && (
              <Button onClick={() => { setIsDetailsOpen(false); handleEdit(detailsCred); }}>
                <Edit className="h-4 w-4 mr-2" />Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
